<template>
  <!--
    Las cifras o las tarjetas que todavía no han llegado.

    kind="stat": la fila de UiStat de lo alto de una lista o de una ficha —la
    misma caja, el mismo reparto (dos por fila en el teléfono)—. Si la pantalla
    ya pinta sus UiStat con `:loading`, no hace falta: cada uno ya guarda su
    sitio. Esto es para cuando las cifras ni siquiera se sabe cuáles son
    (dependen de lo que devuelve la API, o de los módulos instalados).

    kind="card": una rejilla de tarjetas con título y unas líneas, como los
    paneles del inicio o un tablero de tarjetas.
  -->
  <div
    class="sk-cards"
    :class="`sk-cards--${kind}`"
    :style="kind === 'card' ? { '--sk-min': minWidth } : null"
    :role="quiet ? null : 'status'"
    :aria-busy="quiet ? null : 'true'"
    :aria-hidden="quiet ? 'true' : null"
  >
    <span v-if="!quiet" class="sr-only">{{ uiLabel('loading') }}</span>

    <template v-if="kind === 'stat'">
      <div v-for="n in count" :key="n" class="sk-cards__stat" aria-hidden="true">
        <span class="sk-cards__label"><UiSkeleton height="0.7em" :width="STAT_LABEL[n % STAT_LABEL.length]" /></span>
        <span class="sk-cards__value"><UiSkeleton height="80%" :width="STAT_VALUE[n % STAT_VALUE.length]" /></span>
      </div>
    </template>

    <template v-else>
      <div v-for="n in count" :key="n" class="sk-cards__card" :style="{ minHeight: height }" aria-hidden="true">
        <div class="sk-cards__card-head">
          <UiSkeleton height="1em" :width="TITLE[n % TITLE.length]" />
        </div>
        <div class="sk-cards__card-body">
          <UiSkeleton :lines="lines" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import UiSkeleton from './UiSkeleton.vue'
import { uiLabel } from '../utils/labels'

defineProps({
  // stat | card
  kind: {
    type: String,
    default: 'stat',
    validator: value => ['stat', 'card'].includes(value),
  },
  count: { type: Number, default: 4 },
  // Solo para kind="card": líneas de texto bajo el título.
  lines: { type: Number, default: 3 },
  // Solo para kind="card": el ancho mínimo de una tarjeta en la rejilla, y su
  // alto mínimo si la tarjeta de verdad es más alta que sus líneas.
  minWidth: { type: String, default: '16rem' },
  height: String,
  // Dentro de otro esqueleto que ya dice «cargando» (UiSkeletonFile): sin
  // segundo aviso para el lector de pantalla.
  quiet: Boolean,
})

const STAT_LABEL = ['58%', '72%', '50%', '66%']
const STAT_VALUE = ['36%', '52%', '28%', '44%']
const TITLE = ['46%', '60%', '38%', '52%']
</script>

<style scoped>
/* La fila de UiStat: `.stats` de cada pantalla es flex con wrap y gap-3. */
.sk-cards--stat { display: flex; flex-wrap: wrap; gap: var(--space-3); }

/* La caja de UiStat, medida por medida (ver UiStat.vue). */
.sk-cards__stat {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1 1 9rem;
  max-width: 15rem;
  min-width: 8rem;
  padding: var(--space-4) var(--space-5);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
/* Cada renglón con el alto del texto que lo sustituye: el rótulo en xs y la
   cifra en 2xl con interlineado 1.1. */
.sk-cards__label { display: flex; align-items: center; height: calc(var(--text-xs) * 1.55); }
.sk-cards__value { display: flex; align-items: center; height: calc(var(--text-2xl) * 1.1); }

@media (max-width: 640px) {
  .sk-cards__stat {
    flex-basis: calc(50% - var(--space-3)); min-width: 0; max-width: none;
    padding: var(--space-3) var(--space-4);
  }
}

/* `min()` dentro del minmax: sin él, en un teléfono más estrecho que una
   tarjeta la rejilla se sale por la derecha (regla de `npm run check`). */
.sk-cards--card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(var(--sk-min), 100%), 1fr));
  gap: var(--space-4);
}

/* La piel de UiCard: borde, radio grande, sombra pequeña, cabecera con raya. */
.sk-cards__card {
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}
.sk-cards__card-head {
  display: flex;
  align-items: center;
  min-height: calc(var(--text-lg) * 1.25 + 2 * var(--space-4));
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.sk-cards__card-body { flex: 1; padding: var(--space-5) var(--space-5) var(--space-6); }
</style>
